import React, { useState, useEffect, useRef } from "react";
import { useProjects } from "../../context/ProjectContext"; // 1. Importar hook
import { toast } from "sonner";

export const ProjectForm = ({ isOpen, onClose }) => {
    const { createProject } = useProjects(); // 2. Extraer método del contexto

    const [projectName, setProjectName] = useState("");
    const [selectedIcon, setSelectedIcon] = useState("📁");
    const [selectedColor, setSelectedColor] = useState("#64748b");
    const [adding, setAdding] = useState(false);

    const modalRef = useRef(null);

    const quickIcons = ["📁", "📚", "💼", "🚀", "🏡", "🧠", "🎯", "🛒"];
    const quickColors = ["#64748b", "#ef4444", "#f97316", "#3b82f6", "#10b981", "#a855f7", "#ec4899", "#eab308"];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!projectName.trim()) {
            toast.error("El nombre del proyecto no puede estar vacío.");
            return;
        }

        setAdding(true);
        try {
            // 3. Ejecutar la llamada a la base de datos
            await createProject({
                name: projectName.trim(),
                icon: selectedIcon,
                color: selectedColor
            });
            
            toast.success(`Proyecto "${projectName}" creado exitosamente.`);
            
            // Limpieza
            setProjectName("");
            setSelectedIcon("📁");
            setSelectedColor("#64748b");
            onClose();
        } catch (error) {
            toast.error(`Error al crear el proyecto: ${error.message}`);
        } finally {
            setAdding(false);
        }
    };

    // ... (El resto del renderizado HTML/Tailwind del ProjectForm permanece igual)

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Fondo oscuro traslúcido */}
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"></div>

            {/* Caja del Modal */}
            <div 
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full p-5 relative z-10 animate-fadeIn"
            >
                {/* Título */}
                <div className="flex items-center space-x-2 mb-4">
                    <i className="fas fa-folder-plus text-blue-600 text-base"></i>
                    <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Añadir Proyecto</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Input: Nombre del Proyecto */}
                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1">Nombre</label>
                        <input
                            type="text"
                            placeholder="Ej. Universidad, Trabajo..."
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 font-medium bg-slate-50 focus:bg-white transition-all"
                            maxLength={40}
                            autoFocus
                        />
                    </div>

                    {/* Selector de Íconos Rápidos */}
                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1">Ícono representativo</label>
                        <div className="flex flex-wrap gap-2">
                            {quickIcons.map(icon => (
                                <button
                                    key={icon}
                                    type="button"
                                    onClick={() => setSelectedIcon(icon)}
                                    className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm border transition-all
                                        ${selectedIcon === icon ? "bg-blue-50 border-blue-400 scale-105" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                                >
                                    {icon}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Selector de Colores Rápidos */}
                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1.5">Color del proyecto</label>
                        <div className="flex flex-wrap gap-2.5">
                            {quickColors.map(color => (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => setSelectedColor(color)}
                                    className={`w-6 h-6 rounded-full transition-transform relative flex items-center justify-center hover:scale-110 shadow-sm`}
                                    style={{ backgroundColor: color }}
                                >
                                    {selectedColor === color && (
                                        <i className="fas fa-check text-[10px] text-white"></i>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Footer del Modal */}
                    <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={adding || !projectName.trim()}
                            className={`px-4 py-1.5 text-xs font-semibold text-white rounded-lg shadow-md transition-colors
                                ${adding || !projectName.trim()
                                    ? "bg-blue-300 cursor-not-allowed shadow-none"
                                    : "bg-blue-600 hover:bg-blue-700"}`}
                        >
                            {adding ? "Guardando..." : "Añadir"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};